import React, { useEffect, useMemo, useState } from "react";
import { Container, Row, Col, Button, Input } from "reactstrap";
import { Modal, ModalHeader, ModalBody } from "reactstrap";
import { useDispatch, useSelector } from "react-redux";

import { hasPermission } from "../Utils/UtilityFunctions";
import { getAllSO } from "../../slices/SO/GetSO/thunk";
import { stockoutRequestSO } from "../../slices/SO/ApprovalRequestSO/thunk";
import { getNotification } from "../../slices/Notification/GetNotification/thunk";
import { useColumnSettings } from "../Utils/useColumnSettings";
import { formatCurrency } from "../Utils/Currency";

import SOForm from "./SOForm";
import SOStockOutForm from "./SOStockOutForm";
import SOTable from "./Pages/SOTable";
import CommonModal from "../Utils/CommonModal";

const STATUS_FILTER = "pending, waiting for approval, approved, completed";

const formatSO = (so, index) => ({
  id: index + 1,
  soNumber: so.orderid,
  location: so.fromlocationname || "-",
  locationId: so.fromlocation || "-",
  items: so.items?.length || 0,
  totalAmt:
    so.items?.reduce((sum, item) => sum + Number(item.totalprice || 0), 0) || 0,
  itemData: (so.items || []).map((item) => ({
    id: item.id,
    sku: item.sku,
    itemid: item.itemid,
    item: item.itemname,
    fromlocation: so.fromlocationname || "-",
    fromLocationId: so.fromlocation,
    orderedquantity: item.orderedquantity,
    approvedquantity: item.approvedquantity || 0,
    notificationid: item.notificationid,
    unitPrice: item.unitprice,
    netQty: item.itemcount,
    total: item.totalprice,
    status:
      item.status === "pending"
        ? "Pending"
        : item.status === "waiting for approval"
          ? "Waiting Approval"
          : item.status === "approved"
            ? "Approved"
            : item.status === "completed"
              ? "Completed"
              : item.status,
    rawStatus: item.status,
    action:
      item.status === "pending"
        ? "request"
        : item.status === "waiting for approval"
          ? "approve"
          : item.status === "approved"
            ? "stockout"
            : item.status === "completed"
              ? "completed"
              : null,
  })),
});

function SOListings() {
  const dispatch = useDispatch();
  const rawPermissions = useSelector(
    (state) => state.permissions.permissionsMap,
  );
  const roleName = localStorage.getItem("roleName");
  const operatorName = localStorage.getItem("tradingName");

  const permissionsMap = new Map(
    rawPermissions.map(([key, value]) => [key, new Set(value)]),
  );
  const salesorderPermission = hasPermission(
    permissionsMap,
    "transactions",
    "salesorder",
  );
  const notificationsPermission = hasPermission(permissionsMap, "monitoringandcontrol", "notifications");

  const soList = useSelector((state) => state.GetSO.soList || []);

  const [searchText, setSearchText] = useState("");
  const [searchValue, setSearchValue] = useState("");
  const [allSOData, setAllSOData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: 10 });

  const [modalOpen, setModalOpen] = useState(false);
  const [editData, setEditData] = useState(null);
  const [isEdit, setIsEdit] = useState(false);
  const [isApprove, setIsApprove] = useState(false);

  const [stockOutModalOpen, setStockOutModalOpen] = useState(false);
  const [stockOutData, setStockOutData] = useState(null);

  const [commonModalOpen, setCommonModalOpen] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);
  const [modalType, setModalType] = useState("");

  useEffect(() => {
    dispatch(
      getAllSO(
        pagination.pageSize,
        pagination.pageIndex + 1,
        STATUS_FILTER,
        setLoading,
      ),
    );
  }, [dispatch, pagination.pageSize, pagination.pageIndex]);

  useEffect(() => {
    const formattedData = (soList.salesOrders || []).map(formatSO);

    setAllSOData((prev) => {
      const merged = [...prev, ...formattedData];
      return merged.filter(
        (item, index, self) =>
          index === self.findIndex((x) => x.soNumber === item.soNumber),
      );
    });
  }, [soList]);

  const soData = useMemo(() => {
    const formattedData = (soList.salesOrders || []).map(formatSO);

    if (!searchValue.trim()) return formattedData;

    return allSOData.filter((so) => {
      const search = searchValue.toLowerCase();
      return (
        so.soNumber?.toLowerCase().includes(search) ||
        so.location?.toLowerCase().includes(search) ||
        so.itemData?.some(
          (item) =>
            item.item?.toLowerCase().includes(search) ||
            item.sku?.toLowerCase().includes(search),
        )
      );
    });
  }, [soList, searchValue, allSOData]);

  const columns = useMemo(
    () => [
      {
        accessorKey: "soNumber",
        header: "Sales Order Number",
        size: 220,
        minSize: 220,
        maxSize: 220,
      },
      {
        accessorKey: "location",
        header: "Location",
        size: 180,
        minSize: 180,
        maxSize: 180,
      },
      {
        accessorKey: "items",
        header: "No. of Items",
        size: 120,
        minSize: 120,
        maxSize: 120,
      },
      {
        accessorKey: "totalAmt",
        header: "Total Amount",
        size: 150,
        minSize: 150,
        maxSize: 150,
        Cell: ({ cell }) => formatCurrency(cell.getValue()),
      },
    ],
    [],
  );

  const { CustomViewModal } = useColumnSettings("salesorder", columns);

  const refreshSOList = () =>
    dispatch(
      getAllSO(
        pagination.pageSize,
        pagination.pageIndex + 1,
        STATUS_FILTER,
        setLoading,
      ),
    );

  const toggleModal = () => {
    setModalOpen((prev) => !prev);
    if (modalOpen) {
      setEditData(null);
      setIsEdit(false);
      setIsApprove(false);
    }
  };

  const handleEdit = (rowData, parentRow) => {
    setIsEdit(true);
    setEditData({
      orderId: parentRow.soNumber,
      locationName: {
        label: parentRow.location,
        value: parentRow.location,
      },
      status: {
        label: parentRow.status,
        value: parentRow.status,
      },
      items: [
        {
          itemName: { label: rowData.item, value: rowData.item },
          sku: rowData.sku,
          orderedQty: rowData.orderedquantity,
          approvedQty: rowData.orderedquantity,
          unitPrice: rowData.unitPrice,
          itemId: rowData.itemid,
          itemCount: rowData.netQty,
          total: rowData.total,
          notificationid: rowData.notificationid,
        },
      ],
    });
    setModalOpen(true);
  };

  const handleRequest = (rowData, parentRow) => {
    setSelectedRow({ rowData, parentRow });
    setModalType("request");
    setCommonModalOpen(true);
  };

  const handleApprove = (rowData, parentRow) => {
    handleEdit(rowData, parentRow);
    setIsApprove(true);
  };

  const handleStockOut = (rowData, parentRow) => {
    setStockOutData({
      itemName: rowData.item,
      itemId: rowData.itemid,
      sku: rowData.sku,
      quantity: rowData.netQty,
      stockOutQty: rowData.approvedquantity || rowData.netQty,
      fromlocation: rowData.fromlocation,
      fromlocationId: rowData.fromLocationId,
      soNumber: parentRow.soNumber,
    });
    setStockOutModalOpen(true);
  };

  const handleApprovalReqAction = () => {
    const { rowData, parentRow } = selectedRow;

    if (modalType === "request") {
      const payload = {
        orderid: parentRow.soNumber,
        fromlocation: parentRow.locationId,
        fromlocationname: parentRow.location,
        items: [
          {
            itemid: rowData.itemid,
            itemname: rowData.item,
            quantity: rowData.netQty,
            stockoutquantity: rowData.orderedquantity,
            message: "Request for StockOut",
            type: "Stockout",
          },
        ],
      };

      dispatch(
        stockoutRequestSO(operatorName, payload, setLoading, () => {
          setCommonModalOpen(false);
          if (notificationsPermission) dispatch(getNotification());
          refreshSOList();
        }),
      );
    }
  };

  return (
    <React.Fragment>
      <div id="csms">
        <Container fluid>
          <Row>
            {/* Page header */}
            <div className="d-flex justify-content-between align-items-center mb-3">
              <h5 className="rv-page-title m-0">Order List</h5>
            </div>

            {/* Search + action bar */}
            <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center mb-3 gap-3">
              <div className="d-flex align-items-center w-100 gap-2">
                <Input
                  type="text"
                  placeholder="Type to search here"
                  value={searchText}
                  onChange={(e) => setSearchText(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") setSearchValue(searchText);
                  }}
                  style={{
                    maxWidth: "500px",
                    height: "36px",
                    borderRadius: "4px",
                  }}
                />
                <Button
                  className="btn-label rv-primary-btn rounded-1 px-1 ps-4 py-1 text-nowrap"
                  onClick={() => setSearchValue(searchText)}
                >
                  <i className="ri-search-line label-icon align-middle fs-16 me-1 fw-bold"></i>
                  <span className="ps-2 ms-2 me-1 fw-bold">Search</span>
                </Button>
              </div>

              <div className="d-flex align-items-center">
                {salesorderPermission?.has("export") && (
                  <Button className="btn-label rv-secondary-btn rounded-1 px-1 ps-4 py-1 text-nowrap mx-1">
                    <i className="ri-download-line label-icon secondary-label-icon align-middle fs-16 me-2 fw-bold"></i>
                    <span className="px-2 ps-3 ms-2 me-1 fw-bold">Export</span>
                  </Button>
                )}
                {salesorderPermission?.has("create") && (
                  <Button
                    className="btn-label rv-primary-btn rounded-1 px-1 ps-4 py-1 w-100 w-md-auto text-nowrap mx-1"
                    onClick={toggleModal}
                  >
                    <i className="ri-add-fill label-icon align-middle fs-16 me-1 fw-bold"></i>
                    <span className="ps-2 ms-2 me-1 fw-bold">
                      Add Sales Order
                    </span>
                  </Button>
                )}
              </div>
            </div>

            {/* Main table */}
            <Col lg={12}>
              <SOTable
                soData={soData}
                totalCount={soList?.totalCount || 0}
                pagination={pagination}
                onPaginationChange={setPagination}
                loading={loading}
                columns={columns}
                salesorderPermission={salesorderPermission}
                onEdit={handleEdit}
                onRequest={handleRequest}
                onApprove={handleApprove}
                onStockOut={handleStockOut}
              />
            </Col>

            {CustomViewModal()}
          </Row>
        </Container>
      </div>

      <Modal isOpen={modalOpen} toggle={toggleModal} centered size="md">
        <ModalHeader
          toggle={toggleModal}
          style={{ borderBottom: "1px solid #FD7545", padding: "16px 20px" }}
        >
          <span style={{ color: "#FD7545", fontWeight: "600" }}>
            {isApprove
              ? "Approve Sales Order"
              : isEdit
                ? "Update Sales Order"
                : "Add Sales Order"}
          </span>
        </ModalHeader>
        <ModalBody
          className="overflow-auto"
          style={{ maxHeight: "80vh", padding: "20px 20px 0px 20px" }}
        >
          <SOForm
            closeModal={toggleModal}
            editData={editData}
            isEdit={isEdit}
            isApprove={isApprove}
            refreshSOList={refreshSOList}
          />
        </ModalBody>
      </Modal>

      <Modal
        isOpen={stockOutModalOpen}
        toggle={() => setStockOutModalOpen(false)}
        centered
        size="md"
      >
        <ModalHeader
          toggle={() => setStockOutModalOpen(false)}
          style={{ borderBottom: "1px solid #FD7545", padding: "16px 20px" }}
        >
          <span style={{ color: "#FD7545", fontWeight: "600" }}>
            Stock Out Product
          </span>
        </ModalHeader>
        <ModalBody>
          <SOStockOutForm
            stockOutData={stockOutData}
            closeModal={() => setStockOutModalOpen(false)}
            refreshSOList={refreshSOList}
          />
        </ModalBody>
      </Modal>

      <CommonModal
        open={commonModalOpen}
        toggle={() => setCommonModalOpen(false)}
        modalHeader={modalType === "request" ? "Request Approval" : ""}
        messageForPopUp={
          modalType === "request"
            ? "Are you sure you want to request approval for this product?"
            : ""
        }
        buttonText={modalType === "request" ? "Request Approval" : ""}
        modalAction={handleApprovalReqAction}
        isSave={true}
      />
    </React.Fragment>
  );
}

export default SOListings;
